import React from 'react';
import { useParams } from 'react-router-dom';
import type { AuthUser, BranchDetailsItem } from '@/api/models';
import { useBranchDetailsQuery } from '@/api/queries';
import { canImportProducts } from '@/pages/branches/list/branchImportAccess';
import {
    useCatalogImport,
    type CatalogImportBranch,
} from '@/pages/catalogImport/store';
import { SHELL_IMPORT_STEPS } from '@/pages/onboarding/components/stepStrip';
import { PATH } from '@/router/PATH';
import { getUser } from '@/utils/authSession';

/** The in-shell page's steps, each a segment under the page's path. */
export type BranchImportStep = (typeof SHELL_IMPORT_STEPS)[number];

export const isBranchImportStep = (
    value: string | undefined,
): value is BranchImportStep =>
    (SHELL_IMPORT_STEPS as readonly string[]).includes(value ?? '');

/** Where each step's ways lead on the in-shell page. */
export type BranchImportStepPaths = {
    file: { backPath: string; nextPath: string };
    names: { backPath: string; nextPath: string };
    products: { backPath: string; nextPath: string };
    result: { backPath: string; anotherFilePath: string; nextPath: string };
};

/**
 * The wizard's import steps with the branch fixed: there is no Branch step
 * before File, so Upload's Back goes to the branch list, and Result goes on
 * to the branch list (Back to Branches) or starts another file at File on
 * the same branch.
 */
export const branchImportStepPaths = (
    branchId: string,
): BranchImportStepPaths => {
    const step = (name: BranchImportStep) =>
        PATH.BRANCHES.IMPORT_STEP.BIND(branchId, name);
    return {
        file: { backPath: PATH.BRANCHES.ROOT, nextPath: step('names') },
        names: { backPath: step('file'), nextPath: step('products') },
        products: { backPath: step('names'), nextPath: step('result') },
        result: {
            backPath: step('products'),
            anotherFilePath: step('file'),
            nextPath: PATH.BRANCHES.ROOT,
        },
    };
};

export type BranchImportAccess =
    | { kind: 'notFound' }
    | { kind: 'forbidden' }
    | { kind: 'allowed'; branch: CatalogImportBranch };

/**
 * Whether this user may import into the branch the address names:
 *
 * - **Not found** when no live branch has the id: unknown, deleted or
 *   archived (the list leaves archived branches out, and an archived branch
 *   cannot take stock it could never sell).
 * - **Forbidden** when the row menu's rule (`canImportProducts`) would not
 *   offer it: a BranchAdmin of another branch, a Seller, no session.
 * - Otherwise allowed, with the branch the page seeds into its store.
 *
 * The server still decides: a refusal of the branch on a preview or the
 * import (`BRANCH_NOT_ALLOWED`) shows the same forbidden notice.
 */
export const branchImportAccessOf = (
    branches: readonly Pick<BranchDetailsItem, 'id' | 'name' | 'archived'>[],
    branchId: string,
    user: AuthUser | null,
): BranchImportAccess => {
    const branch = branches.find(({ id }) => id === branchId);
    if (!branch || branch.archived) {
        return { kind: 'notFound' };
    }
    if (!canImportProducts(user, branch)) {
        return { kind: 'forbidden' };
    }
    return { kind: 'allowed', branch: { id: branch.id, name: branch.name } };
};

export type BranchImportPageState =
    | {
          status: 'loading';
          /** The step the address names, for the strip meanwhile. */
          step: BranchImportStep | null;
      }
    | { status: 'error'; onRetry: () => void }
    | { status: 'notFound' }
    | { status: 'forbidden' }
    | {
          status: 'ready';
          /** The branch every file on this page goes into. */
          branch: CatalogImportBranch;
          step: BranchImportStep;
          paths: BranchImportStepPaths;
      };

/** Where the branch list stands for this page: still coming, failed, or read. */
export type BranchLookup = 'loading' | 'error' | BranchImportAccess;

type PageStateInput = {
    /** The server refused the branch to this user (`scopeRefused`). */
    scopeRefused: boolean;
    /** The store's branch once it is this page's; `null` before seeding. */
    seededBranch: CatalogImportBranch | null;
    lookup: BranchLookup;
    step: BranchImportStep | null;
    paths: BranchImportStepPaths;
    onRetry: () => void;
};

/**
 * What the page shows. A refusal by the server wins; a seeded branch runs
 * the flow whatever the list says now; before that, the list decides. An
 * allowed branch reads as loading until it is seeded.
 */
export const branchImportPageStateOf = ({
    scopeRefused,
    seededBranch,
    lookup,
    step,
    paths,
    onRetry,
}: PageStateInput): BranchImportPageState => {
    if (scopeRefused) {
        return { status: 'forbidden' };
    }
    if (seededBranch !== null) {
        return {
            status: 'ready',
            branch: seededBranch,
            step: step ?? 'file',
            paths,
        };
    }
    if (lookup === 'loading') {
        return { status: 'loading', step };
    }
    if (lookup === 'error') {
        return { status: 'error', onRetry };
    }
    return lookup.kind === 'allowed'
        ? { status: 'loading', step }
        : { status: lookup.kind };
};

export type UseBranchImportPageResult = {
    page: BranchImportPageState;
    /**
     * Where the page sends an address that names no step (the bare page,
     * which the row menu opens, or an unknown segment): File. `null` on a
     * step.
     */
    redirectTo: string | null;
};

/**
 * The in-shell import page of one branch, inside its own
 * `CatalogImportProvider`:
 *
 * - **The step** is the first segment under the page's path (the route's
 *   splat); anything else goes to File.
 * - **The branch** comes from the branch list (`useBranchDetailsQuery`, the
 *   whole set the pickers share), and the access rule decides between not
 *   found, forbidden and the flow (`branchImportAccessOf`).
 * - **Seeding.** An allowed branch is written to the store once, and the
 *   steps render only after it is there, so Upload never opens without a
 *   branch. From then on the page runs on the store's branch: a later read
 *   of the list (a refetch on focus that fails, say) never takes the flow
 *   away mid-import.
 * - **Refusal.** When the server refuses the branch to this user on a
 *   preview or the import, the store says so (`scopeRefused`) and the page
 *   shows the forbidden notice in place of the step.
 */
export const useBranchImportPage = (
    branchId: string,
): UseBranchImportPageResult => {
    const splat = useParams()['*'] ?? '';
    const segment = splat.split('/')[0];
    const step = isBranchImportStep(segment) ? segment : null;
    const { state, actions } = useCatalogImport();
    const query = useBranchDetailsQuery();
    const user = React.useMemo(() => getUser(), []);

    const lookup = React.useMemo<BranchLookup>(() => {
        if (query.data === undefined) {
            return query.isError ? 'error' : 'loading';
        }
        return query.data.success
            ? branchImportAccessOf(query.data.response.items, branchId, user)
            : 'error';
    }, [branchId, query.data, query.isError, user]);

    const seededBranch = state.branch?.id === branchId ? state.branch : null;
    const toSeed =
        seededBranch === null &&
        typeof lookup === 'object' &&
        lookup.kind === 'allowed'
            ? lookup.branch
            : null;
    React.useEffect(() => {
        if (toSeed !== null) {
            actions.setBranch(toSeed);
        }
    }, [actions, toSeed]);

    const paths = React.useMemo(
        () => branchImportStepPaths(branchId),
        [branchId],
    );
    const { refetch } = query;
    const onRetry = React.useCallback(() => {
        void refetch();
    }, [refetch]);

    return {
        page: branchImportPageStateOf({
            scopeRefused: state.scopeRefused,
            seededBranch,
            lookup,
            step,
            paths,
            onRetry,
        }),
        redirectTo:
            step === null
                ? PATH.BRANCHES.IMPORT_STEP.BIND(branchId, 'file')
                : null,
    };
};
